import React, { useState, useEffect, Fragment } from 'react';
import { postDataToTheServer } from '../../../helpers/api';
import AppURL from '../../../constants/AppURL';
import Container from '../../../constants/Container';

const ManageUnitListReportView = () => {
    const [onManasulList, setOnMahasulList] = useState<any[]>([]);
    const [year, setYear] = useState<number>(2082);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [abortController, setAbortController] = useState<AbortController | null>(null);

    const fetchDataList = async (year: number) => {
        // Cancel previous request if it exists
        if (abortController) {
            abortController.abort();
        }

        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append("year", year.toString());

            const response = await postDataToTheServer(
                AppURL.FrontOfficeURL + 'front/customer/manage/unit/list/view/',
                formData,
            );

            setOnMahasulList(response.data || []);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDataList(year);

        // Cleanup function
        return () => {
            if (abortController) {
                abortController.abort();
            }
        };
    }, [year]);

    return (
        <Container padding>
            <div className="mb-2 flex items-center gap-4">
                <label htmlFor="yearInput" className="font-medium text-sm font-public-sans">
                    Select Year:
                </label>
                <input
                    id="yearInput"
                    type="number"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value) || 2082)}
                    className="border rounded px-3 py-1 w-100"
                    min="2000"
                    max="2100"
                />
                {isLoading && (
                    <span className="text-blue-500">Loading...</span>
                )}
            </div>

            <ManageUnitListView data={onManasulList} />
        </Container>
    );
};

const ManageUnitListView = React.memo(({ data }: { data: any[] }) => {
    const [selectedRow, setSelectedRow] = useState<number | null>(null);

    const monthColors = [
        'bg-red-200', 'bg-orange-200', 'bg-yellow-200', 'bg-green-200',
        'bg-emerald-200', 'bg-teal-200', 'bg-cyan-200', 'bg-sky-200',
        'bg-blue-200', 'bg-indigo-200', 'bg-purple-200', 'bg-pink-200',
    ];

    const months = [
        { id: '1', name: 'Baishakh' }, { id: '2', name: 'Jestha' }, { id: '3', name: 'Ashadh' },
        { id: '4', name: 'Shrawan' }, { id: '5', name: 'Bhadra' }, { id: '6', name: 'Ashwin' },
        { id: '7', name: 'Kartik' }, { id: '8', name: 'Mangsir' }, { id: '9', name: 'Poush' },
        { id: '10', name: 'Magh' }, { id: '11', name: 'Falgun' }, { id: '12', name: 'Chaitra' },
    ];

    const formatConsumption = (monthData: any) => {
        if (!Array.isArray(monthData) || monthData.length === 0 || !Array.isArray(monthData[0])) {
            return ['-', '-', '-'];
        }
        const [unitTotal = '-', consumeUnit = '-', consumeAmt = '-'] = monthData[0] || [];
        return [unitTotal, consumeUnit, consumeAmt];
    };

    const verticalTotals = Array(12).fill(0);

    return data.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No data available</div>
    ) : (
        <div className="overflow-x-auto rounded-lg h-[70vh]">
            <table className="min-w-full bg-white text-nowrap text-[12px] border border-gray-200">
                <thead className="bg-gray-100 sticky top-0 z-10">
                    <tr>
                        <th rowSpan={2} className="py-1 px-2 border-b bg-gray-100">SN</th>
                        <th rowSpan={2} className="py-1 px-2 border-b bg-gray-100">Member Name</th>
                        <th rowSpan={2} className="py-1 px-2 border-b bg-gray-100">Address</th>
                        <th rowSpan={2} className="py-1 px-2 border-b bg-gray-100">Ward</th>
                        {months.map(month => (
                            <th key={month.id} colSpan={3} className="py-1 px-2 border-b text-center sticky top-0 bg-gray-100 z-10">
                                {month.name}
                            </th>
                        ))}
                        <th rowSpan={2} className="py-1 px-2 border-b bg-gray-100">Total Amt</th>
                    </tr>
                    <tr>
                        {months.map((month, index) => (
                            <Fragment key={month.id}>
                                <th className={`py-1 px-2 border text-center ${monthColors[index]} bg-white`}>Tot.</th>
                                <th className={`py-1 px-2 border text-center ${monthColors[index]} bg-white`}>Cons.</th>
                                <th className={`py-1 px-2 border text-center ${monthColors[index]} bg-white`}>Amt.</th>
                            </Fragment>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {data.map((member, rowIndex) => {
                        let rowTotal = 0;

                        return (
                            <tr
                                key={member.member_sn}
                                onClick={() => setSelectedRow(rowIndex)}
                                className={`cursor-pointer ${
                                    selectedRow === rowIndex ? 'bg-green-200' : 'hover:bg-gray-50'
                                }`}
                            >
                                <td className="py-1 px-2 border text-center">{member.member_sn}</td>
                                <td className="py-1 px-2 border">{member.mem_name}</td>
                                <td className="py-1 px-2 border">{member.marga_tole}</td>
                                <td className="py-1 px-2 border text-center">{member.ward_no}</td>

                                {months.map((month, index) => {
                                    const [total, consUnit, amount] = formatConsumption(member[month.id]);
                                    const amt = isNaN(parseFloat(amount)) ? 0 : parseFloat(amount);
                                    if (amt > 0) {
                                        verticalTotals[index] += amt;
                                        rowTotal += amt;
                                    }

                                    return (
                                        <Fragment key={`${member.member_sn}-${month.id}`}>
                                            <td className={`py-1 px-2 border text-center ${monthColors[index]}`}>{total}</td>
                                            <td className={`py-1 px-2 border text-center ${monthColors[index]}`}>{consUnit}</td>
                                            <td className={`py-1 px-2 border text-center ${monthColors[index]}`}>
                                                {amount === '-' ? '-' : `Rs ${amt}`}
                                            </td>
                                        </Fragment>
                                    );
                                })}

                                <td className="py-1 px-2 border text-start font-semibold bg-yellow-100">
                                    Rs {rowTotal.toFixed(2)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>

                <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                        <td colSpan={4} className="py-1 px-2 border text-right">Total</td>
                        {verticalTotals.map((amt, i) => (
                            <Fragment key={`total-${i}`}>
                                <td className={`py-1 px-2 border ${monthColors[i]}`} />
                                <td className={`py-1 px-2 border ${monthColors[i]}`} />
                                <td className={`py-1 px-2 border text-start ${monthColors[i]}`}>
                                    Rs {amt.toFixed(2)}
                                </td>
                            </Fragment>
                        ))}
                        <td className="py-1 px-2 border text-start bg-green-200">
                            Rs {verticalTotals.reduce((a, b) => a + b, 0).toFixed(2)}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
});



export default ManageUnitListReportView;